import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';
import { ChecklistItem, EquipmentAnomalySummaryItem, FVIEquipmentItem, IncludeSummary,  User } from '../../../utils/models';

import { getTutorials } from '../../../store/tutorials';
import { reArrangeInspectionItemAnomalies, reArrangeGeneralPhotos, validateWorkOrderNumber } from '../../../utils/utilities';
import { DEFAULT_REPORT_TITLE, MINIMUM_ITEM_SIZE_FOR_CHUNK_NOTICE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';

import { getCurrentUser } from '../../../services/authService';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import ChunkReport from '../../blocks/chunk-report';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import EquipmentItems from './equipment-items';
import FviEquipmentReport from './fviEquipmentReport';
import FVICoverData from '../../themed/fvi-cover-data';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import Switch from '../../themed/switch';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useIncompleteInspectedSections from '../../../hooks/useIncompleteInspectedSections';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomFVIEquipmentReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [certificates, setCertificates] = useState<ICertificate>();
	const [includeSummary, setIncludeSummary] = useState<IncludeSummary>('include');
	const [equipmentItems, setEquipmentItems] = useState<FVIEquipmentItem[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [executiveSummary, setExecutiveSummary] = useState('');

	const [isChunkNoticeVisible, setChunkNoticeVisible] = useState(false);
	const [itemsToPublish, setItemsToPublish] = useState<FVIEquipmentItem[]>([]);

	const { role } = getCurrentUser() as User;
	const { isLastInspectedItemComplete } = useIncompleteInspectedSections(equipmentItems)

	const handleEquipmentItemChanges = (newEquipmentItems: FVIEquipmentItem[]) => {
		setEquipmentItems(newEquipmentItems);
		autoSave.saveReport();
	};
	
	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		
		if (role.approveReports && equipmentItems.length > MINIMUM_ITEM_SIZE_FOR_CHUNK_NOTICE && itemsToPublish.length === 0) {
			return setChunkNoticeVisible(true);
		}
		
		togglePreviewReport();
	};

	const handleToggleSummary = () => {
		setIncludeSummary((previous) => previous === 'include' ? 'exclude' : 'include');
	};

	const reportDetails: any = useMemo(() => {
		return {
			projectDetails,
			coverData,
			certificates,
			equipmentItems,
			executiveSummary,
			includeSummary,
			reportTitle,
		};
	}, [projectDetails, certificates, coverData, equipmentItems, executiveSummary, includeSummary, reportTitle]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			const supportConditionMissing = equipmentItems.filter((item) => !item.anomalySummary.supportCondition);
			
			if (supportConditionMissing.length > 0) {
				const IDs = supportConditionMissing.map((item) => item.equipmentNumber).join(', ');
				return toastService.error(`Provide a valid support condition for: ${IDs}`);
			}

			if (!validateWorkOrderNumber(coverData.workOrderNumber)) {
				return toastService.error('Enter a valid work order number!');
			}

			if (!isLastInspectedItemComplete) {
				return toastService.error('Please fill all fields first!');
			}
		}

		setPreviewReport((p: boolean) => !p);
	}, [isLastInspectedItemComplete, coverData.workOrderNumber, equipmentItems]);

	const handleResetChunkedItemsAndClosePreview = useCallback(() => {
		setItemsToPublish([]);
		togglePreviewReport();
	}, [togglePreviewReport]);
	
	const handleCloseChunkNoticeAndPreviewReport = useCallback(() => {
		setChunkNoticeVisible(false);
		togglePreviewReport();
	}, [togglePreviewReport]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	useEffect(() => {
		const cachedReport = autoSave.oldReportData as typeof reportDetails | null;

		if (!cachedReport) {
			if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
			return;
		}

		if (cachedReport.certificates) {
			setCertificates(cachedReport.certificates);
		}

		if (cachedReport.includeSummary) {
			setIncludeSummary(cachedReport.includeSummary);
		}

		const rearrangedAnomalies = reArrangeInspectionItemAnomalies(cachedReport.equipmentItems) as any[];
		const rearrangedGeneralPhotos = reArrangeGeneralPhotos(rearrangedAnomalies) as any[];

		setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
		setEquipmentItems(rearrangedGeneralPhotos);
		setReportTitle(cachedReport.reportTitle);
		setExecutiveSummary(cachedReport.executiveSummary);

		const lastPhotoNumber = cachedReport.equipmentItems.reduce((a: number, c: FVIEquipmentItem) => a + c.generalPhotos.length, 0);
		dispatch(setLastPhotoNumber(lastPhotoNumber));

	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.EFVIP));
	}, [dispatch]);

	return (
		<Fragment>
			{projectDetails && (
				<ChunkReport
					groupName="equipmentItems"
					projectId={projectDetails._id}
					projectModule={projectDetails.module}
					reportDetails={reportDetails}
					items={equipmentItems}
					chunkKeyId="equipmentNumber" 
					isNoticeVisible={isChunkNoticeVisible}
					onSetChunkedItems={(items) => setItemsToPublish(items)}
					onCloseNotice={() => setChunkNoticeVisible(false)}
					onCancelChunkReport={handleCloseChunkNoticeAndPreviewReport} 
					setFullPreviewVisible={() => setPreviewReport(true)}
				/>
			)}

			<Modal isVisible={previewReport} onClose={handleResetChunkedItemsAndClosePreview}>
				<FviEquipmentReport {...reportDetails} equipmentItems={itemsToPublish.length > 0 ? itemsToPublish : equipmentItems} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilFVIEquipment[projectDetails.organisationId]}
					isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilFVIEquipment && role.approveReports}
					subtitle='Update the Cover Data information. This is the default data used on all ExxonMobilFVI Equipment Reports'
				/>
			)}

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm
					projectTitle={projectDetails?.projectTitle}
					onAdd={setReportTitle}
				/>
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm
					initialData={coverData}
					onAddCoverForm={setCoverData}
				/>
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<div className="d-flex justify-content-end mt-2">
					<Switch 
						checked={includeSummary === 'include'}
						label='Include Inspection Breakdown'
						onChange={handleToggleSummary} 
					/>
				</div>
				
				<div className="my-4 divider"></div>

				<ExecutiveSummaryForm
					initialSummary={executiveSummary}
					onProceed={setExecutiveSummary}
				/>
			</Zippy>

			<CustomCertificateForm calibrationRequired={false} certificates={certificates} onChangeCertificate={setCertificates} />

			<EquipmentItems equipmentItems={equipmentItems} onChangeEquipmentItems={handleEquipmentItemChanges} />
			
			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export const EQUIPMENT_ANOMALY: EquipmentAnomalySummaryItem = {
	pipeBody: '',
	noOfPoints: 0,
	pitting: '',
	noOfPittingLocations: 0,
	flanges: '',
	fasteners: '',
	valves: '',
	corrosionUnderPipeSupport: '',
	noOfCups: 0,
	supportCondition: '',
	compositeWrapOnLine: 'no',
	others: '',
	description: '',
	extentOfCoatingBreakdown: '',
	
	pipe: '',
	guage: '',
	dish: '',
	topDish: '',
	bottomDish: '',
	nozzle: '',
	namePlate: '',
	skirt: '',
	structure: '',
	externalProtection: '',
	insulation: '',
	
	noOfPipe: 0,
	noOfGuage: 0,
	noOfDish: 0,
	noOfTopDish: 0,
	noOfBottomDish: 0,
	noOfNozzle: 0,
	noOfNamePlate: 0,
	noOfSkirt: 0,
	noOfStructure: 0,
	noOfExternalProtection: 0,
	noOfInsulation: 0,
};

export const EQUIPMENT_CHECKLIST: ChecklistItem[] = [
	{
		id: '1',
		lineNumber: 'Body or Shell and Transition Cone',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '2',
		lineNumber: 'Head / Dish end',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '3',
		lineNumber: 'Skirt / Support / Support legs',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '4',
		lineNumber: "Small Piping Connections Including Gussets",
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '5',
		lineNumber: 'Platform, Ladder Or Other Attachments Welds To Vessel',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '6',
		lineNumber: 'Lifting Lugs/Trunions',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '7',
		lineNumber: 'Insulation Support/Vacuum Rings',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '8',
		lineNumber: 'Nozzles And Manways Including Reinforcement Pads',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '9',
		lineNumber: 'Coating Condition',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '10',
		lineNumber: 'Insulation/Weatherproofing / Insulation Type',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '11',
		lineNumber: 'Leak, if any',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'external surface',
	},
	{
		id: '12',
		lineNumber: 'Platforms And Handrails',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '13',
		lineNumber: 'Ladders',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '14',
		lineNumber: 'Stairways',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '15',
		lineNumber: 'Pipe Supports, Guides And Braces',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '16',
		lineNumber: 'Flannges And Associated Hardware',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '17',
		lineNumber: 'Name Plates',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '18',
		lineNumber: 'Foundation and Foundation boalt',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '19',
		lineNumber: 'Fire proofing / Insulation',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '20',
		lineNumber: 'Guy wires',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '21',
		lineNumber: 'Safety Valve And Associated Piping',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'auxiliary components',
	},
	{
		id: '22',
		lineNumber: 'Level Gauges',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'instrumentation and associated hardware',
	},
	{
		id: '23',
		lineNumber: 'Pressure Indicating Gauges/Instruments',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'instrumentation and associated hardware',
	},
	{
		id: '24',
		lineNumber: 'Thermowells And Temperature Indicating Instruments',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'instrumentation and associated hardware',
	},
];

export default CustomFVIEquipmentReport;
